import { AdConnectorAPIError } from "@/backend/types/backendTypes";
import axiosInstance from "@/lib/axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { z } from "zod";
import { useAuth } from "../useAuth";

const OutbrainEventNameSchema = z.object({
  id: z.number(),
  organisation: z.string(),
  name: z.string(),
  created: z.string(),
  updated: z.string(),
});

const CreateOutbrainEventNameSchema = z.object({
  organisationId: z.string().min(1, "Organisation ID is required"),
  name: z.string().trim().min(1, "Outbrain event name is required"),
});

const GetOutbrainEventNamesSchema = z.object({
  organisationId: z.string().min(1, "Organisation ID is required"),
});

const UpdateOutbrainEventNameSchema = z.object({
  id: z.number().int("Outbrain event-name ID must be an integer"),
  name: z.string().trim().min(1, "Outbrain event name is required"),
});

const DeleteOutbrainEventNameSchema = z.object({
  id: z.number().int("Outbrain event-name ID must be an integer"),
});

export type OutbrainEventName = z.infer<typeof OutbrainEventNameSchema>;
export type CreateOutbrainEventNameInput = z.infer<
  typeof CreateOutbrainEventNameSchema
>;
export type GetOutbrainEventNamesInput = z.infer<
  typeof GetOutbrainEventNamesSchema
>;
export type UpdateOutbrainEventNameInput = z.infer<
  typeof UpdateOutbrainEventNameSchema
>;
export type DeleteOutbrainEventNameInput = z.infer<
  typeof DeleteOutbrainEventNameSchema
>;

const getErrorMessage = (error: AxiosError) => {
  const apiError = error.response?.data as
    | (Partial<AdConnectorAPIError> & { detail?: unknown; error?: string })
    | undefined;

  if (Array.isArray(apiError?.detail)) {
    const firstErr = apiError.detail[0];
    if (firstErr?.msg) {
      return firstErr.msg;
    }
  }

  if (typeof apiError?.detail === "string") {
    return apiError.detail;
  }

  return apiError?.error ?? error.message;
};

const getValidationMessage = (error: z.ZodError) => {
  const firstIssue = error.issues[0];
  if (firstIssue?.path[0] === "id" && firstIssue.code === "invalid_type") {
    return "Outbrain event-name ID is required";
  }

  return firstIssue?.message ?? "Invalid Outbrain event-name input";
};

const fetchOutbrainEventNames = async (input: GetOutbrainEventNamesInput) => {
  try {
    const parsedInput = GetOutbrainEventNamesSchema.parse(input);
    const response = await axiosInstance.get(
      `${process.env.NEXT_PUBLIC_AD_CONNECTOR_V1_BASE_URL}/outbrain/event-name/${parsedInput.organisationId}/get`
    );

    if (response.status !== 200) {
      throw new Error(response?.data?.error ?? "Unknown error occurred.");
    }

    return z.array(OutbrainEventNameSchema).parse(response.data);
  } catch (err) {
    if (err instanceof z.ZodError) {
      throw new Error(getValidationMessage(err));
    }

    if (axios.isAxiosError(err)) {
      if (!err.response) {
        throw err;
      }
      throw new Error(getErrorMessage(err));
    }

    throw err instanceof Error ? err : new Error("Unknown error occurred!");
  }
};

const createOutbrainEventName = async (input: CreateOutbrainEventNameInput) => {
  try {
    const parsedInput = CreateOutbrainEventNameSchema.parse(input);
    const response = await axiosInstance.post(
      `${process.env.NEXT_PUBLIC_AD_CONNECTOR_V1_BASE_URL}/outbrain/event-name/create`,
      {
        organisation_id: parsedInput.organisationId,
        name: parsedInput.name,
      }
    );

    if (response.status !== 200) {
      throw new Error(response?.data?.error ?? "Unknown error occurred.");
    }

    return OutbrainEventNameSchema.parse(response.data);
  } catch (err) {
    if (err instanceof z.ZodError) {
      throw new Error(getValidationMessage(err));
    }

    if (axios.isAxiosError(err)) {
      if (!err.response) {
        throw err;
      }
      throw new Error(getErrorMessage(err));
    }

    throw err instanceof Error ? err : new Error("Unknown error occurred!");
  }
};

const updateOutbrainEventName = async (input: UpdateOutbrainEventNameInput) => {
  try {
    const parsedInput = UpdateOutbrainEventNameSchema.parse(input);
    const response = await axiosInstance.post(
      `${process.env.NEXT_PUBLIC_AD_CONNECTOR_V1_BASE_URL}/outbrain/event-name/${parsedInput.id}/update`,
      {
        name: parsedInput.name,
      }
    );

    if (response.status !== 200) {
      throw new Error(response?.data?.error ?? "Unknown error occurred.");
    }

    return OutbrainEventNameSchema.parse(response.data);
  } catch (err) {
    if (err instanceof z.ZodError) {
      throw new Error(getValidationMessage(err));
    }

    if (axios.isAxiosError(err)) {
      if (!err.response) {
        throw err;
      }
      throw new Error(getErrorMessage(err));
    }

    throw err instanceof Error ? err : new Error("Unknown error occurred!");
  }
};

const deleteOutbrainEventName = async (input: DeleteOutbrainEventNameInput) => {
  try {
    const parsedInput = DeleteOutbrainEventNameSchema.parse(input);
    const response = await axiosInstance.delete(
      `${process.env.NEXT_PUBLIC_AD_CONNECTOR_V1_BASE_URL}/outbrain/event-name/${parsedInput.id}/delete`
    );

    if (response.status !== 200 && response.status !== 204) {
      throw new Error(response?.data?.error ?? "Unknown error occurred.");
    }

    return { success: true };
  } catch (err) {
    if (err instanceof z.ZodError) {
      throw new Error(getValidationMessage(err));
    }

    if (axios.isAxiosError(err)) {
      if (!err.response) {
        throw err;
      }
      throw new Error(getErrorMessage(err));
    }

    throw err instanceof Error ? err : new Error("Unknown error occurred!");
  }
};

export type UseOutbrainEventNamesOptions = {
  enabled?: boolean;
};

export const useOutbrainEventNames = (
  input: GetOutbrainEventNamesInput,
  options?: UseOutbrainEventNamesOptions
) => {
  const { data: authData } = useAuth();

  return useQuery({
    queryKey: ["outbrainEventNames", input.organisationId],
    queryFn: () => fetchOutbrainEventNames(input),
    enabled:
      (options?.enabled ?? true) && !!authData?.token && !!input.organisationId,
    retry: 1,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });
};

export const useCreateOutbrainEventName = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createOutbrainEventName,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["outbrainEventNames"] });
      queryClient.invalidateQueries({ queryKey: ["adAccounts"] });
    },
  });
};

export const useUpdateOutbrainEventName = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateOutbrainEventName,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["outbrainEventNames"] });
      queryClient.invalidateQueries({ queryKey: ["adAccounts"] });
    },
  });
};

export const useDeleteOutbrainEventName = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteOutbrainEventName,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["outbrainEventNames"] });
      queryClient.invalidateQueries({ queryKey: ["adAccounts"] });
    },
  });
};
